import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Overline } from "../components/SectionHeading";
import { AuthShell, authInput, authLabel, authPrimaryBtn, formatDetail } from "../components/auth/AuthShell";
import { WhatsAppOtpPanel } from "../components/auth/WhatsAppOtpPanel";
import { useAuth } from "../context/AuthContext";
import { REGISTER } from "../constants/testIds/auth";

const Field = ({ id, label, hint, testId, ...props }) => (
  <div>
    <label htmlFor={id} className={authLabel}>
      {label} {hint && <span className="font-normal normal-case tracking-normal text-brand-stone">({hint})</span>}
    </label>
    <input id={id} data-testid={testId} className={authInput} {...props} />
  </div>
);

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", whatsapp: "", email: "", password: "", confirm: "", consent: true });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirm) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await register({
        name: form.name.trim(),
        whatsapp: form.whatsapp.trim(),
        email: form.email.trim() || null,
        password: form.password,
        whatsapp_consent: form.consent,
      });
      navigate("/");
    } catch (err) {
      setError(formatDetail(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      testId="register-page"
      image="/brand/heena-square.jpg"
      imageAlt="Heena, the HESHAURA muse, in a burgundy embroidered ensemble"
      imagePosition="object-[50%_18%]"
      quote="“Same you. Brighter always.”"
      caption="Become part of the HESHAURA world"
    >
      <Overline>Account</Overline>
      <h1 className="mt-4 font-serif text-4xl font-medium tracking-tight text-brand-charcoal sm:text-5xl">Join HESHAURA.</h1>
      <p className="mt-3 text-sm text-brand-stone">Create your account for order updates, faster checkout and early access.</p>
      <form onSubmit={onSubmit} className="mt-8 space-y-5">
        <Field id="register-name" label="Full Name" testId={REGISTER.nameInput} type="text" required autoComplete="name" value={form.name} onChange={set("name")} />
        <div>
          <label htmlFor="register-whatsapp" className={authLabel}>
            WhatsApp Number
          </label>
          <div className="mt-2 flex">
            <span className="flex items-center border border-r-0 border-brand-sand bg-brand-cream px-4 text-sm text-brand-stone">+91</span>
            <input
              id="register-whatsapp"
              type="tel"
              required
              inputMode="numeric"
              autoComplete="tel-national"
              value={form.whatsapp}
              onChange={(e) => setForm((f) => ({ ...f, whatsapp: e.target.value.replace(/[^\d\s+-]/g, "") }))}
              placeholder="10-digit number"
              data-testid={REGISTER.whatsappInput}
              className={`${authInput} mt-0`}
            />
          </div>
        </div>
        <Field id="register-email" label="Email" hint="optional" testId={REGISTER.emailInput} type="email" autoComplete="email" value={form.email} onChange={set("email")} />
        <Field id="register-password" label="Password" testId={REGISTER.passwordInput} type="password" required minLength={8} autoComplete="new-password" value={form.password} onChange={set("password")} />
        <Field id="register-password-confirm" label="Confirm Password" testId={REGISTER.passwordConfirmInput} type="password" required minLength={8} autoComplete="new-password" value={form.confirm} onChange={set("confirm")} />
        <label className="flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-brand-stone">
          <input type="checkbox" checked={form.consent} onChange={set("consent")} data-testid={REGISTER.consentCheckbox} className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-[#B09460]" />
          <span>I agree to receive order updates and offers on WhatsApp.</span>
        </label>
        {error && (
          <p className="text-sm font-medium text-destructive" data-testid="register-error">
            {error}
          </p>
        )}
        <button type="submit" disabled={busy} data-testid={REGISTER.submitButton} className={authPrimaryBtn}>
          {busy ? "Creating account…" : "Create Account"}
        </button>
      </form>

      <div className="mt-8 border-t border-brand-sand pt-6">
        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-brand-gold">Verify your WhatsApp</p>
        <p className="mb-4 mt-1 text-xs text-brand-stone">Optional — confirm your number to receive order updates on WhatsApp.</p>
        <WhatsAppOtpPanel compact phone={form.whatsapp} onPhoneChange={() => {}} />
      </div>

      <p className="mt-8 text-center text-sm text-brand-stone">
        Already have an account?{" "}
        <Link to="/login" data-testid={REGISTER.loginLink} className="font-semibold text-brand-gold underline-offset-4 hover:underline">
          Sign in
        </Link>
      </p>
    </AuthShell>
  );
}
